import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "@/pages/auth/api";
import {
  MODEL_RUNTIME_ERROR_MESSAGE,
  ModelRuntimeErrorBlock,
  resolveModelRuntimeErrorMessage,
} from "@/blocks/error/ModelRuntimeErrorBlock";
import { useI18n } from "@/shared/i18n/LanguageContext";

import {
  createModelChoiceTask,
  getModelTaskStatus,
  type ModelChoiceMove,
} from "./modelChoiceApi";
import {
  defaultModelChoiceOption,
  modelChoiceOptions,
  type ModelChoiceOption,
} from "./modelChoiceCatalog";

type ModelChoiceStatus = "idle" | "queued" | "running" | "succeeded" | "failed";

type AnalysisModelChoicePanelProps = {
  moves: string[];
  fen: string | null;
  positionKey: string;
  onSelectMove?: (uci: string) => boolean;
};

const TOP_K_OPTIONS = [3, 5, 8] as const;
const POLL_LIMIT = 70;
const FAST_POLL_ATTEMPTS = 22;
const MID_POLL_ATTEMPTS = 12;
const FAST_POLL_INTERVAL_MS = 90;
const MID_POLL_INTERVAL_MS = 250;
const SLOW_POLL_INTERVAL_MS = 900;

function formatProbability(value: number) {
  return `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`;
}

function isTerminalStatus(status: string) {
  return ["succeeded", "failed", "cancelled", "expired"].includes(status);
}

function pollIntervalForAttempt(attempt: number) {
  if (attempt < FAST_POLL_ATTEMPTS) {
    return FAST_POLL_INTERVAL_MS;
  }
  if (attempt < FAST_POLL_ATTEMPTS + MID_POLL_ATTEMPTS) {
    return MID_POLL_INTERVAL_MS;
  }
  return SLOW_POLL_INTERVAL_MS;
}

function waitForPollInterval(milliseconds: number, signal: AbortSignal) {
  if (signal.aborted) {
    return Promise.reject(new DOMException("Aborted", "AbortError"));
  }

  return new Promise<void>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, milliseconds);

    function onAbort() {
      window.clearTimeout(timeoutId);
      signal.removeEventListener("abort", onAbort);
      reject(new DOMException("Aborted", "AbortError"));
    }

    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function resolveErrorMessage(error: unknown) {
  if (error instanceof DOMException && error.name === "AbortError") {
    return "";
  }
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Model choice unavailable";
}

export function AnalysisModelChoicePanel({
  moves,
  fen,
  positionKey,
  onSelectMove,
}: AnalysisModelChoicePanelProps) {
  const { copy } = useI18n();
  const [selectedModelId, setSelectedModelId] = useState(
    defaultModelChoiceOption?.modelId ?? "",
  );
  const [topK, setTopK] = useState<(typeof TOP_K_OPTIONS)[number]>(3);
  const [isEnabled, setIsEnabled] = useState(false);
  const [status, setStatus] = useState<ModelChoiceStatus>("idle");
  const [moveset, setMoveset] = useState<ModelChoiceMove[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const abortRef = useRef<AbortController | null>(null);

  const selectedModel = useMemo<ModelChoiceOption | undefined>(
    () => modelChoiceOptions.find((option) => option.modelId === selectedModelId),
    [selectedModelId],
  );

  useEffect(() => {
    abortRef.current?.abort();
    setStatus("idle");
    setMoveset([]);
    setErrorMessage("");
  }, [positionKey, selectedModelId, topK, isEnabled]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const runModelChoice = useCallback(async () => {
    if (!selectedModel) {
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus("queued");
    setMoveset([]);
    setErrorMessage("");

    try {
      const created = await createModelChoiceTask(
        {
          modelId: selectedModel.modelId,
          moves,
          fen,
          topK,
          temperature: 0.0,
          elo: selectedModel.elo,
        },
        controller.signal,
      );

      for (let attempt = 0; attempt < POLL_LIMIT; attempt += 1) {
        const task = await getModelTaskStatus(created.task_id, controller.signal);
        if (task.status === "running") {
          setStatus("running");
        }

        if (!isTerminalStatus(task.status)) {
          await waitForPollInterval(
            pollIntervalForAttempt(attempt),
            controller.signal,
          );
          continue;
        }

        if (task.status === "succeeded" && task.result?.policy) {
          setMoveset(task.result.policy.top_k.slice(0, topK));
          setStatus("succeeded");
          return;
        }

        setStatus("failed");
        setErrorMessage(
          resolveModelRuntimeErrorMessage(
            task.error,
            task.error?.message ?? copy.analysis.unavailable,
          ),
        );
        return;
      }

      setStatus("failed");
      setErrorMessage(copy.analysis.timedOut);
    } catch (error) {
      const message = resolveErrorMessage(error);
      if (!message) {
        return;
      }
      setStatus("failed");
      setErrorMessage(resolveModelRuntimeErrorMessage(error, message));
    }
  }, [copy.analysis.timedOut, copy.analysis.unavailable, fen, moves, selectedModel, topK]);

  useEffect(() => {
    if (!isEnabled) {
      abortRef.current?.abort();
      return;
    }

    void runModelChoice();
  }, [isEnabled, positionKey, runModelChoice]);

  const toggleEnabled = () => {
    setIsEnabled((current) => !current);
  };

  const isBusy = status === "queued" || status === "running";
  const statusLabel =
    !isEnabled
      ? copy.analysis.off
      : status === "queued"
      ? copy.analysis.queued
      : status === "running"
        ? copy.analysis.running
        : status === "succeeded"
          ? copy.analysis.done
          : copy.analysis.ready;

  return (
    <section className="analysis-model-choice" aria-label={copy.analysis.modelChoice}>
      <div className="analysis-model-choice__head">
        <div>
          <strong>{copy.analysis.modelChoice}</strong>
        </div>
        <span data-status={status} data-enabled={isEnabled}>
          {status === "failed" ? copy.analysis.offline : statusLabel}
        </span>
      </div>

      <label className="analysis-model-choice__select">
        <span>{copy.analysis.model}</span>
        <select
          value={selectedModelId}
          onChange={(event) => setSelectedModelId(event.target.value)}
        >
          {modelChoiceOptions.map((option) => (
            <option key={option.modelId} value={option.modelId}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <div className="analysis-model-choice__toolbar">
        <div className="analysis-model-choice__segments" aria-label={copy.analysis.topChoices}>
          {TOP_K_OPTIONS.map((value) => (
            <button
              key={value}
              type="button"
              className={topK === value ? "is-active" : ""}
              onClick={() => setTopK(value)}
            >
              {copy.analysis.top} {value}
            </button>
          ))}
        </div>
        <button
          className="analysis-model-choice__power"
          type="button"
          disabled={!selectedModel}
          aria-label={isEnabled ? copy.analysis.turnModelOff : copy.analysis.turnModelOn}
          aria-pressed={isEnabled}
          data-active={isEnabled}
          onClick={toggleEnabled}
        >
          <span />
          {isEnabled ? copy.analysis.on : copy.analysis.off}
        </button>
      </div>

      <div className="analysis-model-choice__list">
        {status === "succeeded" && moveset.length > 0
          ? moveset.map((move) => {
              const rowContent = (
                <>
                  <span>{move.rank}</span>
                  <strong>{move.san}</strong>
                  <div aria-hidden="true">
                    <i style={{ width: formatProbability(move.probability) }} />
                  </div>
                  <em>{formatProbability(move.probability)}</em>
                </>
              );

              return onSelectMove ? (
                <button
                  className="analysis-model-choice__row"
                  type="button"
                  key={`${move.rank}-${move.uci}`}
                  onClick={() => onSelectMove(move.uci)}
                  aria-label={`${copy.analysis.playModelChoice} ${move.san}`}
                >
                  {rowContent}
                </button>
              ) : (
                <div
                  className="analysis-model-choice__row analysis-model-choice__row--static"
                  key={`${move.rank}-${move.uci}`}
                >
                  {rowContent}
                </div>
              );
            })
          : null}

        {isBusy
          ? Array.from({ length: topK }, (_, index) => (
              <div className="analysis-model-choice__row is-loading" key={index}>
                <span>{index + 1}</span>
                <strong />
                <div aria-hidden="true">
                  <i />
                </div>
                <em />
              </div>
            ))
          : null}

        {status === "failed" ? (
          errorMessage === MODEL_RUNTIME_ERROR_MESSAGE ? (
            <ModelRuntimeErrorBlock compact />
          ) : (
            <div className="analysis-model-choice__empty" role="status">
              {errorMessage}
            </div>
          )
        ) : null}

        {status === "idle" ? (
          <div className="analysis-model-choice__empty" role="status">
            {copy.analysis.switchOn}
          </div>
        ) : null}
      </div>
    </section>
  );
}
